import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DanishDatePicker } from "@/components/DanishDatePicker";
import { UserSingleSelect } from "@/components/UserSingleSelect";
import { toast } from "sonner";
import { ArrowLeft, ClipboardList, Upload, FileText, Trash2, History, ChevronDown, ChevronRight, CheckCircle2 } from "lucide-react";
import { daDate, isClosedDate } from "@/lib/date";

const TYPE_LABEL = { kvalitet: "Kvalitet", indkoeb: "Indkøb" };
const DEFAULT_RESP = { indkoeb: "Anna Fuhr", kvalitet: "Tom Sørensen" };
const initials = (n) => (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";

export default function CreateCase({ type = null }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ type: type || "", title: "", description: "", priority: "Normal", due_date: "", work_days: "", responsible_id: null, customer_id: "" });
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [closed, setClosed] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const fixedType = !!type;

  useEffect(() => { api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([])); }, []);
  useEffect(() => { api.get("/customers").then((r) => setCustomers(r.data || [])).catch(() => setCustomers([])); }, []);
  useEffect(() => { api.get("/dashboard").then((r) => setClosed(r.data?.my_created_cases_closed || [])).catch(() => setClosed([])); }, []);
  useEffect(() => { if (f.type && users.length) setF((p) => ({ ...p, responsible_id: p.responsible_id || (users.find((u) => u.name === DEFAULT_RESP[p.type])?.id || null) })); /* eslint-disable-next-line */ }, [users]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const closedList = fixedType ? closed.filter((c) => c.type === type) : closed;

  const deptUsers = useMemo(() => {
    const t = f.type;
    return (users || []).filter((u) => u.active !== false && (u.role === "admin" || (u.departments || []).includes(t)));
  }, [users, f.type]);

  const computedStart = useMemo(() => {
    const d = f.work_days ? parseInt(f.work_days, 10) : 0;
    if (!f.due_date || !d || d < 1) return null;
    const [y, m, dd] = f.due_date.slice(0, 10).split("-").map(Number);
    const dt = new Date(y, m - 1, dd); let rem = d - 1;
    while (rem > 0) { dt.setDate(dt.getDate() - 1); if (dt.getDay() !== 0 && dt.getDay() !== 6 && !isClosedDate(dt)) rem -= 1; }
    return `${String(dt.getDate()).padStart(2, "0")}-${String(dt.getMonth() + 1).padStart(2, "0")}-${dt.getFullYear()}`;
  }, [f.due_date, f.work_days]);

  const submit = async () => {
    if (!f.type) return toast.error("Vælg en afdeling");
    if (!f.title.trim()) return toast.error("Angiv en titel");
    if (!f.responsible_id) return toast.error("Vælg en sagsansvarlig");
    setSaving(true);
    try {
      const body = {
        type: f.type, title: f.title, description: f.description, priority: f.priority,
        due_date: f.due_date || null, work_days: f.work_days ? parseInt(f.work_days, 10) : null,
        customer_id: f.customer_id || null,
        assigned_user_ids: f.responsible_id ? [f.responsible_id] : [],
      };
      const res = await api.post("/cases", body);
      const cid = res.data.id;
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/cases/${cid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Sagen er oprettet");
      nav(`/sag/${cid}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunne ikke oprette sag");
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[640px] rise-in" data-testid="create-case-page">
      <button onClick={() => nav(-1)} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1.5 mb-6"><ArrowLeft className="h-4 w-4" /> Tilbage</button>
      <div className="flex items-center gap-3 mb-8">
        <span className="h-11 w-11 rounded-xl bg-[#F1DEDA] text-[#A83232] flex items-center justify-center shrink-0"><ClipboardList className="h-5 w-5" strokeWidth={1.75} /></span>
        <div>
          <h1 className="text-3xl font-light tracking-tight text-[#1A1919]">{fixedType ? `Ny ${TYPE_LABEL[type].toLowerCase()}sag` : "Ny sag"}</h1>
          <p className="text-sm text-stone-500">Opret en sag som afdelingen kan modtage og styre.</p>
        </div>
      </div>

      <div className="bg-white border border-[#EBE8E3] rounded-2xl p-6 space-y-5">
        {!fixedType && (
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Afdeling *</Label>
            <Select value={f.type} onValueChange={(v) => setF((p) => ({ ...p, type: v, responsible_id: users.find((u) => u.name === DEFAULT_RESP[v])?.id || null }))}>
              <SelectTrigger data-testid="case-type" className="rounded-xl h-11 bg-white"><SelectValue placeholder="Vælg afdeling" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="kvalitet">Kvalitet</SelectItem>
                <SelectItem value="indkoeb">Indkøb</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Titel *</Label>
          <Input data-testid="case-title" value={f.title} onChange={(e) => set("title", e.target.value)} className="rounded-xl h-11 bg-white" placeholder="Fx Afvigelse på svejsning — Hal B" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Beskrivelse</Label>
          <Textarea data-testid="case-desc" value={f.description} onChange={(e) => set("description", e.target.value)} rows={4} className="rounded-xl bg-white resize-none" placeholder="Beskriv sagen og hvad der ønskes udført…" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Prioritet</Label>
            <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger data-testid="case-priority" className="rounded-xl h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{["Lav", "Normal", "Høj"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Deadline</Label>
            <DanishDatePicker value={f.due_date || ""} onChange={(v) => set("due_date", v)} testid="case-due" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Antal arbejdsdage</Label>
            <Input type="number" min="1" data-testid="case-workdays" value={f.work_days} onChange={(e) => set("work_days", e.target.value)} className="rounded-xl h-11 bg-white" placeholder="Fx 5" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Beregnet startdato</Label>
            <div className="rounded-xl h-11 bg-[#FBFAF8] border border-[#EBE8E3] flex items-center px-3 text-sm" data-testid="case-computed-start">
              {computedStart ? <span className="font-semibold text-stone-700">{computedStart}</span> : <span className="text-stone-400">Udfyld deadline + arbejdsdage</span>}
            </div>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Kunde</Label>
          <Select value={f.customer_id} onValueChange={(v) => set("customer_id", v)}>
            <SelectTrigger data-testid="case-customer" className="rounded-xl h-11 bg-white"><SelectValue placeholder="Vælg kunde (valgfrit)" /></SelectTrigger>
            <SelectContent>{[...customers].sort((a, b) => (a.name || "").localeCompare(b.name || "", "da")).map((cu) => <SelectItem key={cu.id} value={cu.id}>{cu.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Sagsansvarlig *</Label>
          <UserSingleSelect users={deptUsers} value={f.responsible_id} onChange={(v) => set("responsible_id", v)} testid="case-responsible" className="h-11" placeholder="Vælg ansvarlig" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Oprettes af</Label>
          <div className="flex items-center gap-2.5 rounded-xl h-11 bg-[#FBFAF8] border border-[#EBE8E3] px-3" data-testid="case-creator">
            <span className="h-7 w-7 rounded-full bg-[#F1DEDA] text-[#A83232] text-[11px] font-semibold flex items-center justify-center shrink-0">{initials(user?.name)}</span>
            <span className="text-sm text-stone-700">{user?.name || "—"}</span>
          </div>
        </div>
        <div className="space-y-2">
          <Label className="text-sm font-medium">Dokumentation</Label>
          <input ref={fileRef} type="file" multiple className="hidden" data-testid="case-file-input" onChange={(e) => { setFiles((p) => [...p, ...Array.from(e.target.files || [])]); e.target.value = ""; }} />
          {files.map((file, i) => (
            <div key={i} className="flex items-center gap-2 rounded-xl border border-[#F3F1EC] px-3 py-2 bg-white" data-testid={`case-file-${i}`}>
              <FileText className="h-4 w-4 text-stone-400 shrink-0" />
              <span className="text-sm text-stone-700 truncate flex-1">{file.name}</span>
              <button type="button" onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))} className="text-stone-300 hover:text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <button type="button" onClick={() => fileRef.current?.click()} data-testid="case-add-file" className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-[#D6D3D1] text-stone-500 hover:text-[#A83232] hover:border-[#A83232] py-2 text-sm font-medium"><Upload className="h-4 w-4" /> Vedhæft fil</button>
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#EBE8E3] rounded-2xl p-5" data-testid="my-closed-cases">
          <button onClick={() => setShowClosed((v) => !v)} className="w-full flex items-center justify-between text-left" data-testid="toggle-my-closed">
            <span className="inline-flex items-center gap-2 text-sm font-medium text-stone-700"><History className="h-4 w-4 text-stone-400" /> Mine løste / lukkede sager ({closedList.length})</span>
            <ChevronDown className={`h-4 w-4 text-stone-400 transition-transform ${showClosed ? "rotate-180" : ""}`} />
          </button>
          {showClosed && (
            <div className="mt-4 space-y-2" data-testid="my-closed-list">
              {closedList.length === 0 && <p className="text-sm text-stone-400 text-center py-3" data-testid="my-closed-empty">Du har ingen løste eller lukkede sager endnu.</p>}
              {closedList.map((c) => {
                const st = (c.status === "Færdig" || c.status === "Løst")
                  ? { bg: "bg-[#E7EADF]", tx: "text-[#7E856C]", dot: "bg-[#7E856C]" }
                  : { bg: "bg-stone-100", tx: "text-stone-500", dot: "bg-stone-300" };
                return (
                  <button key={c.case_id} onClick={() => nav(`/sag/${c.case_id}`)} data-testid={`my-closed-${c.case_id}`} className="group w-full flex items-center gap-3 rounded-xl border border-[#F3F1EC] px-3.5 py-3 hover:border-[#D6D3D1] hover:bg-[#FBFAF8] text-left [transition-property:background-color,border-color] duration-200">
                    <span className="h-9 w-9 rounded-xl bg-[#F3F1EC] text-stone-400 flex items-center justify-center shrink-0"><CheckCircle2 className="h-4.5 w-4.5" strokeWidth={1.75} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="text-sm font-medium text-[#1A1919] truncate block">{c.title}</span>
                      <span className="text-xs text-stone-400 flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span className="inline-flex items-center rounded-full bg-[#F3F1EC] px-2 py-0.5 text-[10px] font-medium text-stone-500">{TYPE_LABEL[c.type] || c.type}</span>
                        {c.deadline ? <span>Deadline {daDate(c.deadline)}</span> : null}
                        {c.responsible_name ? <span>· {c.responsible_name}</span> : null}
                      </span>
                    </span>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium shrink-0 ${st.bg} ${st.tx}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /> {c.status}
                    </span>
                    <ChevronRight className="h-4 w-4 text-stone-300 group-hover:text-[#A83232] [transition-property:color] duration-200 shrink-0" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

      <div className="flex justify-end gap-2 mt-6">
        <Button variant="outline" className="rounded-xl border-[#EBE8E3]" onClick={() => nav(-1)}>Annullér</Button>
        <Button className="rounded-xl font-medium bg-[#A83232] hover:bg-[#8f2a2a]" onClick={submit} disabled={saving} data-testid="case-submit">{saving ? "Opretter…" : "Opret sag"}</Button>
      </div>
    </div>
  );
}
